/**
 * Standard Actions configuration for Dawn.
 *
 * Storefront Renderer injects the Shopify Standard Actions bundle
 * (`window.Shopify.actions.{updateCart,openCart,getCart,…}`). This file
 * overrides the bundle's built-in Dawn refresh path with an explicit,
 * in-theme version so forks that change Dawn's cart contract keep
 * working. Remove this file and the built-in defaults take over.
 *
 *   - openCart   — opens <cart-drawer>; falls back to /cart.
 *   - updateCart — after the Storefront API mutation, refreshes the
 *     affected cart sections and publishes `cart-update` so Dawn's
 *     pubsub subscribers react.
 *   - other actions (getCart, etc.) keep the default implementation.
 */

// Cart custom elements that advertise sections via getSectionsToRender().
// If Dawn adds a new cart custom element, add its tag here.
const DAWN_CART_TAGS = ['cart-drawer', 'cart-items', 'cart-drawer-items', 'cart-notification'];

// Sections that Dawn's own pubsub subscribers refresh (cart.js's
// CartItems#onCartUpdate fetches and replaces these directly when
// cart-update fires; cart-drawer.js's renderContents handles the
// drawer body). We skip them here to avoid double-rendering.
// Format is '<element-tag>:<getSectionsToRender entry id>'.
// If you change which sections those subscribers refresh, update this set.
const DAWN_PUBSUB_REFRESHED_SECTIONS = new Set([
  'cart-drawer:cart-drawer',
  'cart-drawer-items:CartDrawer',
  'cart-items:main-cart-items',
]);

// Walk every mounted Dawn cart custom element, collect the sections it
// wants rendered, and dedupe. Returns a Map keyed by section id, each
// entry pointing at the DOM mount and the selector used to extract the
// fresh fragment.
function collectCartSections() {
  const sections = new Map();

  for (const el of document.querySelectorAll(DAWN_CART_TAGS.join(','))) {
    let entries;
    try {
      entries = el.getSectionsToRender?.();
    } catch {
      continue;
    }

    const tag = el.tagName.toLowerCase();
    for (const entry of entries ?? []) {
      if (DAWN_PUBSUB_REFRESHED_SECTIONS.has(`${tag}:${entry.id}`)) continue;

      const sectionId = entry.section ?? entry.id;
      if (!sectionId || sections.has(sectionId)) continue;

      // Two patterns coexist in getSectionsToRender():
      //   - cart-items style: entry.section is the parent Liquid section
      //     id, entry.selector is a child node inside it.
      //   - cart-drawer / cart-notification style: entry.id IS the mount;
      //     there is no parent wrapper.
      const root = entry.section ? document.getElementById(entry.id) : document;
      if (!root) continue;

      const mount = entry.selector
        ? (root.querySelector(entry.selector) ?? (entry.section ? root : null))
        : document.getElementById(entry.id);
      if (!mount) continue;

      sections.set(sectionId, {
        mount,
        extractSelector: entry.selector || '.shopify-section',
      });
    }
  }

  return sections;
}

// After a Storefront API mutation, refresh every Dawn cart section
// that isn't already refreshed by Dawn's own pubsub subscribers, then
// publish 'cart-update' so the subscribers run.
//
// We always fetch /cart.js (with sections= when we have any) so that
// `cartData` is defined for subscribers. quick-add-bulk.js reads
// `event.cartData.items` unconditionally — publishing without cartData
// makes it throw.
async function refreshDawnCartUI() {
  const sections = collectCartSections();
  const sectionsQuery = sections.size
    ? `?sections=${[...sections.keys()].join(',')}`
    : '';
  // `routes` is a Dawn global, but don't assume it's defined.
  const cartUrl = (typeof routes !== 'undefined' && routes?.cart_url) || '/cart';
  const url = `${cartUrl}.js${sectionsQuery}`;
  const cartData = await fetch(url, { headers: { Accept: 'application/json' } })
    .then((r) => (r.ok ? r.json() : null))
    .catch(() => null);

  if (cartData?.sections) {
    for (const [id, { mount, extractSelector }] of sections) {
      const html = cartData.sections[id];
      if (!html) continue;
      const source = new DOMParser()
        .parseFromString(html, 'text/html')
        .querySelector(extractSelector);
      if (source) mount.replaceChildren(...source.childNodes);
    }
  }

  // Hand off to Dawn's existing subscribers. cartData is the full
  // Cart Ajax payload (items, item_count, token, …) plus sections;
  // quick-add-bulk.js and price-per-item.js read it directly.
  publish(PUB_SUB_EVENTS.cartUpdate, {
    source: 'external-refresh',
    cartData: cartData ?? undefined,
  });
}

function initStandardActions() {
  const actions = window.Shopify?.actions;
  if (!actions) return;

  actions.openCart.configure({
    async handler(defaultHandler) {
      const drawer = document.querySelector('cart-drawer');
      if (drawer && typeof drawer.open === 'function') {
        drawer.open();
        return;
      }
      return defaultHandler();
    },
  });

  actions.updateCart.configure({
    // Dawn doesn't currently listen for shopify:cart:* events, but the
    // bundle requires an eventTarget. document is the conventional root.
    eventTarget: () => document,
    async handler(defaultHandler) {
      const result = await defaultHandler();
      try {
        await refreshDawnCartUI();
      } catch (error) {
        console.error('[Dawn] Standard Actions cart refresh failed; reloading.', error);
        window.location.reload();
      }
      return result;
    },
  });
}

// Run immediately if the standard-actions bundle has already attached
// `Shopify.actions`; otherwise wait for DOMContentLoaded, which fires after
// all module scripts have executed regardless of document order.
if (window.Shopify?.actions) {
  initStandardActions();
} else {
  document.addEventListener('DOMContentLoaded', initStandardActions, { once: true });
}

/* JILL purchase validation v2 — purchase controls stay visible; required personalization is enforced on purchase. */
(() => {
  if (window.__JILL_PURCHASE_VALIDATION_V2__) return;
  window.__JILL_PURCHASE_VALIDATION_V2__ = true;

  const PRODUCT_SCOPE_SELECTOR = 'product-info, quick-add-modal';
  const PURCHASE_CONTROL_SELECTOR = '[data-jill-quantity-target], .product-form__buttons';
  const REQUIRED_SELECTOR = 'input[required], select[required], textarea[required]';
  const ERROR_CLASS = 'jill-purchase-required-error';
  const ERROR_MESSAGE_ATTR = 'data-jill-required-message';

  function message() {
    const lang = (document.documentElement.lang || 'en').toLowerCase();
    if (lang.startsWith('es')) {
      return 'Completa las opciones obligatorias antes de añadir este artículo al carrito.';
    }
    if (lang.startsWith('fr')) {
      return 'Veuillez remplir les options obligatoires avant d’ajouter cet article au panier.';
    }
    return 'Please complete the required options before adding this item to your cart.';
  }

  function productScopeFor(node) {
    return node?.closest?.(PRODUCT_SCOPE_SELECTOR) || null;
  }

  function forceControlVisible(control) {
    if (!control) return;
    if (control.hidden) control.hidden = false;
    if (control.hasAttribute('hidden')) control.removeAttribute('hidden');
    if (control.style.getPropertyValue('display') !== 'block' || control.style.getPropertyPriority('display') !== 'important') {
      control.style.setProperty('display', 'block', 'important');
    }
    if (control.style.getPropertyValue('visibility') === 'hidden') {
      control.style.removeProperty('visibility');
    }
    if (control.style.getPropertyValue('opacity') === '0') {
      control.style.removeProperty('opacity');
    }
    if (control.getAttribute('data-jill-purchase-visible') !== 'true') {
      control.setAttribute('data-jill-purchase-visible', 'true');
    }
  }

  function forcePurchaseControlsVisible(root = document) {
    const scopes = [];
    if (root.matches?.(PRODUCT_SCOPE_SELECTOR)) scopes.push(root);
    root.querySelectorAll?.(PRODUCT_SCOPE_SELECTOR).forEach((scope) => scopes.push(scope));

    scopes.forEach((scope) => {
      scope.querySelectorAll(PURCHASE_CONTROL_SELECTOR).forEach(forceControlVisible);
    });
  }

  function formRequiredControls(form) {
    return Array.from(form?.elements || []).filter((control) => {
      if (!control?.matches?.(REQUIRED_SELECTOR)) return false;
      if (control.disabled || control.willValidate === false) return false;
      return true;
    });
  }

  function isRendered(control) {
    if (!control) return false;
    if (control.type === 'hidden') return false;
    const style = window.getComputedStyle(control);
    if (style.display === 'none' || style.visibility === 'hidden') return false;
    return control.getClientRects().length > 0;
  }

  function markerFor(control) {
    return (
      control.closest('.jill-customization__field, [data-jill-flow-step], fieldset, .product-form__input') || control
    );
  }

  function clearRequiredError(form) {
    const scope = productScopeFor(form) || form;
    scope.querySelectorAll(`.${ERROR_CLASS}`).forEach((node) => node.classList.remove(ERROR_CLASS));
    scope.querySelectorAll('[aria-invalid="true"][data-jill-required-invalid]').forEach((node) => {
      node.removeAttribute('aria-invalid');
      node.removeAttribute('data-jill-required-invalid');
    });

    const wrapper = scope.querySelector(`.product-form__error-message-wrapper[${ERROR_MESSAGE_ATTR}="true"]`);
    if (wrapper) {
      wrapper.setAttribute('hidden', '');
      wrapper.removeAttribute(ERROR_MESSAGE_ATTR);
      const text = wrapper.querySelector('.product-form__error-message');
      if (text) text.textContent = '';
    }
  }

  function showRequiredError(form, control) {
    const scope = productScopeFor(form) || form;
    clearRequiredError(form);

    const marker = markerFor(control);
    marker.classList.add(ERROR_CLASS);
    control.setAttribute('aria-invalid', 'true');
    control.setAttribute('data-jill-required-invalid', 'true');

    let wrapper = scope.querySelector('.product-form__error-message-wrapper');
    let text = wrapper?.querySelector('.product-form__error-message');

    if (!wrapper) {
      wrapper = document.createElement('div');
      wrapper.className = 'product-form__error-message-wrapper';
      wrapper.setAttribute('role', 'alert');
      text = document.createElement('span');
      text.className = 'product-form__error-message';
      wrapper.appendChild(text);
      const buttons = scope.querySelector('.product-form__buttons');
      (buttons || form).insertAdjacentElement('beforebegin', wrapper);
    }

    if (text) text.textContent = message();
    wrapper.setAttribute(ERROR_MESSAGE_ATTR, 'true');
    wrapper.removeAttribute('hidden');

    // Under normal progressive flow the first invalid field is already visible.
    // If an edge case leaves the only invalid field hidden, reveal only its nearest field wrapper.
    if (!isRendered(control)) {
      const reveal = markerFor(control);
      if (reveal.hidden) reveal.hidden = false;
      reveal.removeAttribute('hidden');
      if (reveal.style.display === 'none') reveal.style.removeProperty('display');
    }

    window.requestAnimationFrame(() => {
      const target = markerFor(control);
      target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      try {
        control.focus({ preventScroll: true });
      } catch {
        control.focus?.();
      }
    });
  }

  function firstInvalidRequired(form) {
    const invalid = formRequiredControls(form).filter((control) => !control.checkValidity());
    return invalid.find(isRendered) || invalid[0] || null;
  }

  function blockIfIncomplete(event, form) {
    if (!form) return false;
    const invalid = firstInvalidRequired(form);
    if (!invalid) {
      clearRequiredError(form);
      return false;
    }

    event?.preventDefault?.();
    event?.stopImmediatePropagation?.();
    showRequiredError(form, invalid);
    forcePurchaseControlsVisible(productScopeFor(form) || document);
    return true;
  }

  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;
      if (!form.matches('form[action*="/cart/add"]')) return;
      if (!productScopeFor(form)) return;
      blockIfIncomplete(event, form);
    },
    true,
  );

  // Protect accelerated/dynamic checkout if it is enabled later.
  document.addEventListener(
    'click',
    (event) => {
      const button = event.target.closest?.('.shopify-payment-button button, [data-shopify="payment-button"] button');
      if (!button) return;
      const scope = productScopeFor(button);
      const form = button.closest('form[action*="/cart/add"]') || scope?.querySelector('form[action*="/cart/add"]');
      blockIfIncomplete(event, form);
    },
    true,
  );

  function handleFieldChange(event) {
    const form = event.target?.form;
    if (!form || !productScopeFor(form)) return;

    const marker = markerFor(event.target);
    if (event.target.matches?.(REQUIRED_SELECTOR) && event.target.checkValidity()) {
      marker.classList.remove(ERROR_CLASS);
      event.target.removeAttribute('aria-invalid');
      event.target.removeAttribute('data-jill-required-invalid');
    }

    if (!firstInvalidRequired(form)) clearRequiredError(form);
    window.requestAnimationFrame(() => forcePurchaseControlsVisible(productScopeFor(form)));
  }

  document.addEventListener('input', handleFieldChange, true);
  document.addEventListener('change', handleFieldChange, true);
  document.addEventListener('shopify:section:load', (event) => forcePurchaseControlsVisible(event.target));

  const style = document.createElement('style');
  style.id = 'jill-purchase-validation-v2-style';
  style.textContent = `
    .${ERROR_CLASS} {
      outline: 0.2rem solid currentColor;
      outline-offset: 0.45rem;
      border-radius: 0.4rem;
      scroll-margin-block: 8rem;
    }
  `;
  document.head.appendChild(style);

  let visibilityFrame = 0;
  function scheduleVisibilityRefresh() {
    if (visibilityFrame) return;
    visibilityFrame = window.requestAnimationFrame(() => {
      visibilityFrame = 0;
      forcePurchaseControlsVisible();
    });
  }

  const observer = new MutationObserver((mutations) => {
    if (
      mutations.some((mutation) =>
        productScopeFor(mutation.target) || mutation.target.matches?.(PRODUCT_SCOPE_SELECTOR),
      )
    ) {
      scheduleVisibilityRefresh();
    }
  });
  observer.observe(document.documentElement, { subtree: true, attributes: true, childList: true });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => forcePurchaseControlsVisible(), { once: true });
  } else {
    forcePurchaseControlsVisible();
  }
})();
